import React, { useContext, useState } from 'react';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import { CardActions } from '@material-ui/core';
import Divider from '@material-ui/core/Divider';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import ConfirmationDialogOnClickWrapper from '../../../components/ConfirmationDialogOnClickWrapper';
import DetailItem from '../../../components/DetailItem';
import LoadingIndicator from '../../../components/LoadingIndicator';
import RxFormProvider from '../../../components/RxForm/RxFormProvider';
import { useRxForm } from '../../../components/RxForm/hooks';
import {
  RETAINER_ARCH_LABELS,
  RETAINER_CUT_TYPE_LABELS
} from '../../../components/RxForm/Retainer/constants';
import PrintCompletionDialog from '../../../components/Shipment/Dialogs/PrintCompletionDialog';
import StatusTag from '../../../components/StatusTag';
import { RX_FORM_STATUSES, RX_FORM_STATUSES_ID } from '../../../constants';
import { getColorByStatus, UTCDateTimeToLocalDateTimeReadable } from '../../../helpers';
import createLoadingSelector from '../../../selectors/loading';
import hasPermission from '../../../selectors/hasPermission';
import { fetchPatientAction, updatePatientRxFormAction } from '../../../reducers/patients';
import {
  CANCEL_RX_FORM_PREFIX,
  COMPLETE_RX_FORM_PREFIX,
  cancelRxFormAction,
  completeRxFormAction
} from '../../../reducers/rxForms';
import PatientWorkflowContext from '../context/PatientWorkflowContext';

const useStyles = makeStyles(theme => ({
  requestHeader: {
    display: 'flex',
    padding: theme.spacing(1, 2),
    alignItems: 'center'
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end'
  },
  actionButton: {
    marginLeft: theme.spacing(1)
  }
}));

const RetainerWorkflowCard = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { rxForm, reset } = useRxForm();
  const { patient } = useContext(PatientWorkflowContext);
  const [openFormDialog, setOpenFormDialog] = useState(false);

  const isSaving = useSelector(state =>
    createLoadingSelector([COMPLETE_RX_FORM_PREFIX, CANCEL_RX_FORM_PREFIX])(state)
  );
  const canComplete = useSelector(state =>
    hasPermission(state, { permissions: ['rx-forms.manage', 'rx-forms.complete'] })
  );
  const canCancel = useSelector(state =>
    hasPermission(state, { permissions: ['rx-forms.manage', 'rx-forms.cancel'] })
  );

  if (!rxForm) {
    return <LoadingIndicator />;
  }

  const isInactive =
    [RX_FORM_STATUSES_ID.completed, RX_FORM_STATUSES_ID.canceled].indexOf(rxForm.status_id) !== -1;
  const shippingLocation = rxForm.shipping_location;

  const refreshPatient = updated => {
    dispatch(updatePatientRxFormAction(patient.id, updated));
    dispatch(fetchPatientAction(patient.id));
    reset();
  };

  const handleCancel = () => {
    return dispatch(cancelRxFormAction(rxForm.id)).then(updated => {
      if (updated) {
        refreshPatient(updated);
      }
    });
  };

  const handleComplete = values => {
    return dispatch(
      completeRxFormAction(rxForm.id, {
        tracking_number: values.tracking_number,
        custom_shipping_fee: values.custom_shipping_fee,
        internal_shipping_cost: values.internal_shipping_cost
      })
    ).then(updated => {
      if (!updated) {
        return null;
      }
      setOpenFormDialog(false);
      refreshPatient(updated);
      return updated;
    });
  };

  return (
    <Grid container>
      <Box mt={2} width="100%">
        <Card>
          <div className={classes.requestHeader}>
            <Box>
              <Typography component="h1" variant="h5">
                Retainer
              </Typography>
              <Typography component="div" variant="caption">
                {`Submitted at ${UTCDateTimeToLocalDateTimeReadable(rxForm.created_at)}`}
              </Typography>
            </Box>
            <Box mx={2}>
              <StatusTag
                label={RX_FORM_STATUSES[rxForm.status_id]}
                color={getColorByStatus(RX_FORM_STATUSES[rxForm.status_id])}
              />
            </Box>
          </div>
          <Divider />
          <CardContent>
            <Grid container>
              <DetailItem
                xs={12}
                label="Arch"
                value={RETAINER_ARCH_LABELS[rxForm.jaws_id] || 'N/A'}
                noPadding
              />
              <DetailItem
                xs={12}
                label="Cut"
                value={RETAINER_CUT_TYPE_LABELS[rxForm.retainer_cut_type] || 'N/A'}
                noPadding
              />
              <DetailItem
                xs={12}
                label="Shipping Location"
                value={
                  shippingLocation
                    ? `${shippingLocation.location_name}${
                        shippingLocation.full_address ? ` — ${shippingLocation.full_address}` : ''
                      }`
                    : ''
                }
                noPadding
              />
              <DetailItem
                xs={12}
                label="Shipping Method"
                value={rxForm.shipping_method_label || ''}
                noPadding
              />
              {rxForm.tracking_number && (
                <DetailItem
                  xs={12}
                  label="Tracking Number"
                  value={rxForm.tracking_number}
                  noPadding
                />
              )}
              {rxForm.completed_at && (
                <DetailItem
                  xs={12}
                  label="Completed"
                  value={UTCDateTimeToLocalDateTimeReadable(rxForm.completed_at)}
                  noPadding
                />
              )}
              {rxForm.cancelled_at && (
                <DetailItem
                  xs={12}
                  label="Cancelled"
                  value={UTCDateTimeToLocalDateTimeReadable(rxForm.cancelled_at)}
                  noPadding
                />
              )}
            </Grid>
          </CardContent>
          {!isInactive && (canComplete || canCancel) && (
            <>
              <Divider />
              <CardActions className={classes.actions}>
                {canCancel && (
                  <ConfirmationDialogOnClickWrapper
                    confirmationTitle="Cancel Retainer"
                    confirmationBody="Are you sure you want to cancel this retainer?"
                  >
                    <Button
                      type="button"
                      variant="contained"
                      color="secondary"
                      disabled={isSaving}
                      className={classes.actionButton}
                      onClick={handleCancel}
                    >
                      Cancel
                    </Button>
                  </ConfirmationDialogOnClickWrapper>
                )}
                {canComplete && (
                  <Button
                    type="button"
                    variant="contained"
                    color="primary"
                    disabled={isSaving}
                    className={classes.actionButton}
                    onClick={() => setOpenFormDialog(true)}
                  >
                    Complete
                  </Button>
                )}
              </CardActions>
            </>
          )}
        </Card>
        {canComplete && (
          <PrintCompletionDialog
            onClose={() => setOpenFormDialog(false)}
            open={openFormDialog}
            onContinue={handleComplete}
          />
        )}
      </Box>
    </Grid>
  );
};

const RetainerWorkflow = ({ rxFormId }) => {
  return (
    <RxFormProvider id={rxFormId} loader loaderProps={{ size: 20, withSpacing: false }}>
      <RetainerWorkflowCard />
    </RxFormProvider>
  );
};

RetainerWorkflow.propTypes = {
  rxFormId: PropTypes.number.isRequired
};

export default RetainerWorkflow;
